import React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router';

import { radioUI } from 'platform/forms-system/src/js/web-component-patterns';

const contactMethods = ['text and email', 'text', 'email', 'mail'];

const NoMobilePhoneAlertDescription = () => {
  const mebNewContactInfoUrls = useSelector(
    state => state.form?.data?.mebNewContactInfoUrls,
  );
  const phoneAndEmailPath = mebNewContactInfoUrls
    ? 'phone-email'
    : 'phone-and-email';

  return (
    <va-alert slim status="warning">
      <>
        We can’t send you text message notifications because we don’t have a
        mobile phone number on file for you.
        <Link
          aria-label="Go back and add a mobile phone number"
          to={{
            pathname: phoneAndEmailPath,
          }}
        >
          <va-button
            secondary
            text="Go back and add a mobile phone number"
            class="vads-u-margin-top--1"
          />
        </Link>
      </>
    </va-alert>
  );
};

const educationLetters = {
  uiSchema: {
    'view:educationLettersIntro': {
      'ui:description': (
        <>
          <h3 className="meb-form-page-only">
            Select your notification preferences for education letters
          </h3>
          <p>
            We’ll send you a notification when a decision letter is available,
            and you can always access your education letters on VA.gov. If
            needed, you may request paper copies by mail.
          </p>
          <p>
            <strong>Note:</strong> You can update your preference for electronic
            access to letters and electronic notifications about your letters in
            your VA profile at any time. Please note that if we are unable to
            notify you using your preferred contact method (text/email), we may
            send notification updates via email or by paper mail.
          </p>
          <va-link
            href="/profile"
            text="Go to your profile"
            class="vads-u-margin-top--2"
          />
        </>
      ),
    },
    educationLettersNotificationMethod: {
      ...radioUI({
        title:
          'How would you like to receive notifications about your education letters?',
        tile: true,
        descriptions: {
          mail: 'You’ll only receive paper copies of your letter by mail. If electronic notification preferences need to be updated or changed, updates must be made within your VA profile.',
        },
      }),
      'ui:validations': [
        (errors, field, formData) => {
          const wantsTexts = field.includes('text');
          const phoneExist = !!formData?.contactInfo?.mobilePhone?.contact;
          const isInternational = !['US', 'PR'].includes(
            formData?.contactInfo?.mobilePhone?.countryCode,
          );
          if (wantsTexts && !phoneExist) {
            errors.addError(
              'You can’t select that response because we don’t have a mobile phone number on file for you',
            );
          } else if (wantsTexts && isInternational) {
            errors.addError(
              'You can’t select that response because you have an international mobile phone number',
            );
          }
        },
      ],
    },
    'view:noMobilePhoneAlert': {
      'ui:description': NoMobilePhoneAlertDescription,
      'ui:options': {
        hideIf: formData => {
          const wantsTexts =
            formData?.educationLettersNotificationMethod?.includes('text');

          return !!formData?.contactInfo?.mobilePhone?.contact || !wantsTexts;
        },
      },
    },
    'view:internationalTextMessageAlert': {
      'ui:description': (
        <va-alert slim status="warning">
          You can’t choose to get text message notifications because you have an
          international mobile phone number. At this time, we can send text
          messages about your education benefits only to U.S. mobile phone
          numbers.
        </va-alert>
      ),
      'ui:options': {
        hideIf: formData => {
          const mobilePhone = formData?.contactInfo?.mobilePhone?.contact;
          const isInternational = !['US', 'PR'].includes(
            formData?.contactInfo?.mobilePhone?.countryCode,
          );
          const wantsTexts =
            formData?.educationLettersNotificationMethod?.includes('text');
          return !mobilePhone || !isInternational || !wantsTexts;
        },
      },
    },
    'view:educationLettersTextMessageDisclaimer': {
      'ui:description': (
        <va-additional-info trigger="What to know about text notifications">
          <p>
            Message and data rates may apply. If you want to opt out, text STOP.
            IF you need help, text HELP.
          </p>
        </va-additional-info>
      ),
    },
  },
  schema: {
    type: 'object',
    properties: {
      'view:educationLettersIntro': {
        type: 'object',
        properties: {},
      },
      'view:noMobilePhoneAlert': {
        type: 'object',
        properties: {},
      },
      educationLettersNotificationMethod: {
        type: 'string',
        enum: contactMethods,
        enumNames: [
          'Both text and email (Recommended)',
          'Text',
          'Email',
          'I don’t want electronic notifications',
        ],
        default: 'text and email',
      },
      'view:internationalTextMessageAlert': {
        type: 'object',
        properties: {},
      },
      'view:educationLettersTextMessageDisclaimer': {
        type: 'object',
        properties: {},
      },
    },
    required: ['educationLettersNotificationMethod'],
  },
};

export default educationLetters;
